// pages/Reports.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getIcon } from '../utils/icons';
import api from '../services/api';
import productService from '../services/productService';
import storeService from '../services/storeService';
import { getCategories } from '../services/categoryService';
import reportService from '../services/reportService';
import UnitsAnalyticsReport from '../components/reports/UnitsAnalyticsReport';
import ImagesAnalyticsReport from '../components/reports/ImagesAnalyticsReport';

/**
 * Локальный сервис для работы с отчетами (расширенный функционал)
 */
const localReportService = {
  /**
   * Получение статистики для дашборда
   * @returns {Promise<Object>} - Данные статистики
   */
  getDashboardStats: async () => {
    return await reportService.getDashboardStats();
  },

/**
 * Получение истории цен на товар
 * @param {string} productId - ID товара
 * @param {string} startDate - Начальная дата (опционально)
 * @param {string} endDate - Конечная дата (опционально)
 * @returns {Promise<Array>} - История цен
 */
getProductPriceHistory: async (productId, startDate, endDate) => {
  return await reportService.getProductPriceHistory(productId, startDate, endDate);
},

/**
 * Получение истории цен в магазине
 * @param {number} storeId - ID магазина
 * @param {string} startDate - Начальная дата (опционально)
 * @param {string} endDate - Конечная дата (опционально)
 * @returns {Promise<Array>} - История цен
 */
getStorePriceHistory: async (storeId, startDate, endDate) => {
  return await reportService.getStorePriceHistory(storeId, startDate, endDate);
},

  /**
   * Получение аналитики по категориям
   * @returns {Promise<Array>} - Данные по категориям
   */
  getCategoryAnalytics: async () => {
    return await reportService.getCategoryAnalytics();
  },

/**
 * Получение сводки по ценникам
 * @param {string} startDate - Начальная дата (опционально)
 * @param {string} endDate - Конечная дата (опционально)
 * @returns {Promise<Object>} - Сводка по ценникам
 */
getPriceTagsSummary: async (startDate, endDate) => {
  return await reportService.getPriceTagsSummary(startDate, endDate);
},

  /**
   * Подготовка данных для отчета "Динамика цен"
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Данные для отчета
   */
  preparePriceDynamicsReport: async (filters) => {
    // Если выбран товар, получаем историю цен для этого товара
    if (filters.productId) {
      const priceHistory = await localReportService.getProductPriceHistory(
        filters.productId, 
        filters.startDate, 
        filters.endDate
      );
      
      // Если данные отсутствуют, возвращаем null
      if (!priceHistory || !priceHistory.length) {
        return null;
      }
      
      // Преобразуем данные в формат для отчета
      const data = {
        labels: [],
        datasets: []
      };
      
      // Группируем данные по магазинам
      const storeGroups = {};
      
      priceHistory.forEach(item => {
        const date = new Date(item.recordedAt).toLocaleDateString();
        
        if (!data.labels.includes(date)) {
          data.labels.push(date);
        }
        
        if (!storeGroups[item.store?.name || item.storeId]) {
          storeGroups[item.store?.name || item.storeId] = {
            name: `${item.product?.name || 'Товар'} (${item.store?.name || 'Магазин #' + item.storeId})`,
            data: {}
          };
        }
        
        storeGroups[item.store?.name || item.storeId].data[date] = item.price;
      });
      
      // Преобразуем группы в наборы данных
      Object.values(storeGroups).forEach(store => {
        const prices = data.labels.map(date => store.data[date] || null);
        data.datasets.push({
          name: store.name,
          data: prices
        });
      });
      
      // Сортируем даты
      data.labels.sort((a, b) => new Date(a) - new Date(b));
      
      return data;
    } 
    // Если выбран магазин, получаем историю цен для этого магазина
    else if (filters.storeId) {
      const priceHistory = await localReportService.getStorePriceHistory(
        filters.storeId,
        filters.startDate,
        filters.endDate
      );
      
      // Если данные отсутствуют, возвращаем null
      if (!priceHistory || !priceHistory.length) {
        return null;
      }
      
      // Преобразуем данные в формат для отчета
      const data = {
        labels: [],
        datasets: []
      };
      
      // Группируем данные по товарам
      const productGroups = {};
      
      priceHistory.forEach(item => {
        const date = new Date(item.recordedAt).toLocaleDateString();
        
        if (!data.labels.includes(date)) {
          data.labels.push(date);
        }
        
        if (!productGroups[item.product?.id || item.productId]) {
          productGroups[item.product?.id || item.productId] = {
            name: item.product?.name || `Товар #${item.productId}`,
            data: {}
          };
        }
        
        productGroups[item.product?.id || item.productId].data[date] = item.price;
      });
      
      // Преобразуем группы в наборы данных
      Object.values(productGroups).forEach(product => {
        const prices = data.labels.map(date => product.data[date] || null);
        data.datasets.push({
          name: product.name,
          data: prices
        });
      });
      
      // Сортируем даты
      data.labels.sort((a, b) => new Date(a) - new Date(b));
      
      return data;
    }
    
    // Если ничего не выбрано, возвращаем null
    return null;
  },

  /**
   * Подготовка данных для отчета "Сравнение магазинов"
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Данные для отчета
   */
  prepareStoreComparisonReport: async (filters) => {
    try {
      const data = await reportService.getStoreComparisonReport(filters);
      
      if (!data || data.length === 0) {
        return null;
      }

      // Если выбран конкретный товар, data будет массивом объектов с информацией о магазинах
      if (filters.productId) {
        return {
          stores: data.map(item => item.storeName || 'Неизвестный магазин'),
          products: [{
            name: data[0]?.productName || 'Товар',
            prices: data.map(item => {
              // Используем среднюю цену, если есть, иначе минимальную или максимальную
              return item.avgPrice || item.minPrice || item.maxPrice || 0;
            })
          }]
        };
      }
      
      // Если общий отчет, data будет массивом товаров с магазинами
      if (Array.isArray(data) && data.length > 0 && data[0].stores) {
        // Для категории или общего отчета
        const allStores = [];
        data.forEach(product => {
          if (product.stores && Array.isArray(product.stores)) {
            product.stores.forEach(store => {
              if (!allStores.find(s => s.storeId === store.storeId)) {
                allStores.push(store);
              }
            });
          }
        });

        return {
          stores: allStores.map(store => store.storeName || 'Неизвестный магазин'),
          products: data.map(product => ({
            name: product.productName || 'Товар',
            prices: allStores.map(store => {
              const productStore = product.stores?.find(s => s.storeId === store.storeId);
              return productStore ? productStore.price : null;
            })
          }))
        };
      }

      return {
        stores: [],
        products: []
      };
    } catch (error) {
      console.error('Ошибка при получении отчета сравнения магазинов:', error);
      return null;
    }
  },

  /**
   * Экспорт отчета в CSV/Excel/PDF
   * @param {string} reportType - Тип отчета
   * @param {Object} filters - Фильтры
   * @param {string} format - Формат экспорта (csv, excel, pdf)
   */
  exportReport: async (reportType, filters, format) => {
    try {
      // Используем новый API для экспорта
      return await reportService.exportReport(reportType, format);
    } catch (error) {
      console.error('Ошибка при экспорте отчета:', error);
      return {
        success: false,
        message: `Ошибка при экспорте отчета: ${error.message}`
      };
    }
  }
};

/**
 * Страница отчетов и аналитики
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState('store-comparison');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({});
  const [filters, setFilters] = useState({
    productId: '',
    storeId: '',
    categoryId: '',
    startDate: '',
    endDate: ''
  });
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);

  // Загрузка данных для отчетов
  useEffect(() => {
    fetchFiltersData();
    fetchReportData();
  }, [activeTab]);

  /**
   * Загрузка данных для фильтров
   */
  const fetchFiltersData = async () => {
    try {
      setError(null);
      // Загружаем реальные данные из API
      const [productsResponse, storesResponse, categoriesResponse] = await Promise.all([
        productService.getProducts({ pageSize: 100 }),
        storeService.getStores(),
        getCategories()
      ]);
      
      // Обрабатываем новый формат пагинации API
      const productsArray = Array.isArray(productsResponse) ? productsResponse : (productsResponse.items || productsResponse.data || []);
      const storesArray = Array.isArray(storesResponse) ? storesResponse : (storesResponse.items || storesResponse.data || []);
      const categoriesArray = Array.isArray(categoriesResponse) ? categoriesResponse : (categoriesResponse.items || categoriesResponse.data || []);
      
      setProducts(productsArray);
      setStores(storesArray);
      setCategories(categoriesArray);
    } catch (err) {
      console.error('Ошибка при загрузке данных для фильтров:', err);
      setError('Не удалось загрузить данные для фильтров. Повторите попытку позже.');
      
      // Устанавливаем пустые списки
      setProducts([]);
      setStores([]);
      setCategories([]);
    }
  };

  /**
   * Загрузка данных для активного отчета
   */
  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'store-comparison') {
        try {
          const data = await localReportService.prepareStoreComparisonReport(filters);
          setReportData(prevData => ({
            ...prevData,
            'store-comparison': data
          }));
        } catch (storeCompErr) {
          console.error('Ошибка при загрузке отчета сравнения магазинов:', storeCompErr);
          setReportData(prevData => ({
            ...prevData,
            'store-comparison': null
          }));
        }
      } else if (activeTab === 'price-comparison') {
        try {
          const data = await reportService.getPriceComparisonReport(filters);
          setReportData(prevData => ({
            ...prevData,
            'price-comparison': data
          }));
        } catch (priceCompErr) {
          console.error('Ошибка при загрузке отчета сравнения цен:', priceCompErr);
          setReportData(prevData => ({
            ...prevData,
            'price-comparison': null
          }));
        }
      } else if (activeTab === 'user-activity') {
        try {
          const data = await reportService.getUserActivityReport(filters);
          setReportData(prevData => ({
            ...prevData,
            'user-activity': data
          }));
        } catch (userActivityErr) {
          console.error('Ошибка при загрузке отчета активности пользователей:', userActivityErr);
          setReportData(prevData => ({
            ...prevData,
            'user-activity': null
          }));
        }
      } else if (activeTab === 'category-analytics') {
        try {
          const data = await localReportService.getCategoryAnalytics();
          
          // Если данные отсутствуют, устанавливаем null
          if (!data || !data.length) {
            setReportData(prevData => ({
              ...prevData,
              'category-analytics': null
            }));
            return;
          }
          
          // Преобразуем данные API в формат для отчета
          const formattedData = {
            categories: data.map(item => ({
              name: item.categoryName,
              productsCount: item.productCount,
              priceTagsCount: item.priceTagCount,
              avgPrice: item.avgPrice || 0
            }))
          };
          
          setReportData(prevData => ({
            ...prevData,
            'category-analytics': formattedData
          }));
        } catch (categoryErr) {
          console.error('Ошибка при загрузке аналитики по категориям:', categoryErr);
          setReportData(prevData => ({
            ...prevData,
            'category-analytics': null
          }));
        }
      }
    } catch (err) {
      console.error('Ошибка при загрузке данных отчета:', err);
      setError('Не удалось загрузить данные отчета. Повторите попытку позже.');
      
      // Устанавливаем null для отчета
      setReportData(prevData => ({
        ...prevData,
        [activeTab]: null
      }));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Обработка изменения фильтров
   * @param {Object} e - Событие изменения поля формы
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  /**
   * Применение фильтров
   * @param {Object} e - Событие отправки формы
   */
// Обновленная функция handleApplyFilters
const handleApplyFilters = (e) => {
  e.preventDefault();
  
  // Проверяем и очищаем пустые даты
  const updatedFilters = { ...filters };
  
  if (updatedFilters.startDate === '') {
    updatedFilters.startDate = null;
  }
  
  if (updatedFilters.endDate === '') {
    updatedFilters.endDate = null;
  }
  
  // Если обе даты указаны, убедимся, что конечная дата не раньше начальной
  if (updatedFilters.startDate && updatedFilters.endDate) {
    const startDate = new Date(updatedFilters.startDate);
    const endDate = new Date(updatedFilters.endDate);
    
    if (endDate < startDate) {
      toast.error('Конечная дата не может быть раньше начальной');
      return;
    }
  }
  
  setFilters(updatedFilters);
  fetchReportData();
};

  /**
   * Экспорт отчета в CSV
   */
  const handleExportCsv = async () => {
    try {
      const result = await localReportService.exportReport(activeTab, filters, 'csv');
      toast.success(result.message);
    } catch (err) {
      console.error('Ошибка при экспорте отчета в CSV:', err);
      toast.error('Не удалось экспортировать отчет. Пожалуйста, попробуйте позже.');
    }
  };

  /**
   * Экспорт отчета в Excel
   */
  const handleExportExcel = async () => {
    try {
      const result = await localReportService.exportReport(activeTab, filters, 'xlsx');
      toast.success(result.message);
    } catch (err) {
      console.error('Ошибка при экспорте отчета в Excel:', err);
      toast.error('Не удалось экспортировать отчет. Пожалуйста, попробуйте позже.');
    }
  };

  /**
   * Экспорт отчета в PDF
   */
  const handleExportPdf = async () => {
    try {
      const result = await localReportService.exportReport(activeTab, filters, 'pdf');
      toast.success(result.message);
    } catch (err) {
      console.error('Ошибка при экспорте отчета в PDF:', err);
      toast.error('Не удалось экспортировать отчет. Пожалуйста, попробуйте позже.');
    }
  };

  /**
   * Компонент для отображения сообщения об отсутствии данных
   * @returns {JSX.Element} - Компонент
   */
  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center py-10">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <p className="text-lg text-gray-500 font-medium">Нет данных для отображения</p>
      <p className="text-gray-400 text-center mt-2">
        Попробуйте изменить параметры фильтрации<br />или выбрать другой отчет
      </p>
    </div>
  );

  /**
   * Рендер отчета "Динамика цен"
   * @returns {JSX.Element} - Компонент для рендеринга
   */
  const renderPriceDynamicsReport = () => {
    const data = reportData['price-dynamics'];
    if (!data) {
      return <NoDataMessage />;
    }
    
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">Динамика цен на товары</h3>
        
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Товар</th>
                {data.labels.map((month, idx) => (
                  <th key={idx} className="px-4 py-2 text-right">{month}</th>
                ))}
                <th className="px-4 py-2 text-right">Изменение</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.datasets.map((dataset, idx) => {
                // Находим первое и последнее не-null значение
                const validPrices = dataset.data.filter(price => price !== null);
                const firstPrice = validPrices.length > 0 ? validPrices[0] : 0;
                const lastPrice = validPrices.length > 0 ? validPrices[validPrices.length - 1] : 0;
                const change = firstPrice > 0 ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;
                
                return (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{dataset.name}</td>
                    {dataset.data.map((price, i) => (
                      <td key={i} className="px-4 py-2 text-right">
                        {price !== null ? `${price.toFixed(2)} ₽` : '-'}
                      </td>
                    ))}
                    <td className={`px-4 py-2 text-right font-medium ${change > 0 ? 'text-red-600' : change < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                      {change > 0 ? '+' : ''}{change.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="h-60 flex items-center justify-center">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-gray-500">
                Здесь будет график динамики цен.<br/>
                В реальном приложении можно использовать Chart.js, Recharts или другую библиотеку.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Рендер отчета "Сравнение магазинов"
   * @returns {JSX.Element} - Компонент для рендеринга
   */
  const renderStoreComparisonReport = () => {
    const data = reportData['store-comparison'];
    if (!data || !data.stores || data.stores.length === 0 || !data.products || data.products.length === 0) {
      return <NoDataMessage />;
    }
    
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">Сравнение цен в магазинах</h3>
        
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Товар</th>
                {data.stores.map((store, idx) => (
                  <th key={idx} className="px-4 py-2 text-right">{store}</th>
                ))}
                <th className="px-4 py-2 text-right">Разница, %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.products.map((product, idx) => {
                // Находим минимальную и максимальную цену, игнорируя null значения
                const validPrices = product.prices.filter(price => price !== null);
                const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
                const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;
                const diff = minPrice > 0 ? ((maxPrice - minPrice) / minPrice) * 100 : 0;
                
                return (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{product.name}</td>
                    {product.prices.map((price, i) => (
                      <td key={i} className={`px-4 py-2 text-right ${price === minPrice ? 'text-green-600 font-medium' : price === maxPrice ? 'text-red-600 font-medium' : ''}`}>
                        {price !== null ? `${price.toFixed(2)} ₽` : '-'}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right font-medium">
                      {diff.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="h-60 flex items-center justify-center">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500">
                Здесь будет сравнительная диаграмма цен в разных магазинах.<br/>
                В реальном приложении можно использовать гистограмму из библиотеки визуализации.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Рендер отчета "Аналитика по категориям"
   * @returns {JSX.Element} - Компонент для рендеринга
   */
  const renderCategoryAnalyticsReport = () => {
    const data = reportData['category-analytics'];
    if (!data || !data.categories || data.categories.length === 0) {
      return <NoDataMessage />;
    }
    
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">Аналитика по категориям</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {data.categories.map((category, idx) => (
            <div key={idx} className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-md font-medium mb-2">{category.name}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Товаров</p>
                  <p className="text-xl font-medium">{category.productsCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ценников</p>
                  <p className="text-xl font-medium">{category.priceTagsCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ср. цена</p>
                  <p className="text-xl font-medium">{category.avgPrice.toFixed(2)} ₽</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ценников на товар</p>
                  <p className="text-xl font-medium">
                    {category.productsCount > 0 
                      ? (category.priceTagsCount / category.productsCount).toFixed(1) 
                      : '0.0'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="h-60 flex items-center justify-center">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
              <p className="text-gray-500">
                Здесь будет круговая диаграмма распределения товаров по категориям.<br/>
                В реальном приложении можно использовать библиотеку визуализации.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Рендер отчета "Сравнение цен"
   * @returns {JSX.Element} - Компонент для рендеринга
   */
  const renderPriceComparisonReport = () => {
    const data = reportData['price-comparison'];
    if (!data || !data.length) {
      return <NoDataMessage />;
    }
    
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">Анализ ценовых диапазонов товаров</h3>
        
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left">Товар</th>
                  <th className="px-4 py-2 text-right">Мин. цена</th>
                  <th className="px-4 py-2 text-right">Ср. цена</th>
                  <th className="px-4 py-2 text-right">Макс. цена</th>
                  <th className="px-4 py-2 text-right">Общая ср. цена</th>
                  <th className="px-4 py-2 text-right">Разброс цен</th>
                  <th className="px-4 py-2 text-right">Вариация, %</th>
                  <th className="px-4 py-2 text-right">Ценников</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{item.productName}</td>
                    <td className="px-4 py-2 text-right text-green-600">{item.minPrice.toFixed(2)} ₽</td>
                    <td className="px-4 py-2 text-right">{item.avgPrice.toFixed(2)} ₽</td>
                    <td className="px-4 py-2 text-right text-red-600">{item.maxPrice.toFixed(2)} ₽</td>
                    <td className="px-4 py-2 text-right font-medium">{item.overallAvgPrice.toFixed(2)} ₽</td>
                    <td className="px-4 py-2 text-right">{item.priceRange.toFixed(2)} ₽</td>
                    <td className={`px-4 py-2 text-right font-medium ${item.priceVariation > 50 ? 'text-red-600' : item.priceVariation > 25 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {item.priceVariation.toFixed(1)}%
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500">{item.totalPriceTags}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium mb-2 text-green-600">Низкая вариация (&lt; 25%)</h4>
            <p className="text-2xl font-bold">{data.filter(item => item.priceVariation < 25).length}</p>
            <p className="text-sm text-gray-500">товаров со стабильными ценами</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium mb-2 text-yellow-600">Средняя вариация (25-50%)</h4>
            <p className="text-2xl font-bold">{data.filter(item => item.priceVariation >= 25 && item.priceVariation <= 50).length}</p>
            <p className="text-sm text-gray-500">товаров с умеренным разбросом</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium mb-2 text-red-600">Высокая вариация (&gt; 50%)</h4>
            <p className="text-2xl font-bold">{data.filter(item => item.priceVariation > 50).length}</p>
            <p className="text-sm text-gray-500">товаров с большим разбросом цен</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h4 className="font-medium mb-4">Топ товары с наибольшей ценовой вариацией</h4>
          <div className="space-y-3">
            {data
              .sort((a, b) => b.priceVariation - a.priceVariation)
              .slice(0, 5)
              .map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium">{item.productName}</span>
                  <div className="text-right">
                    <span className={`font-bold ${item.priceVariation > 50 ? 'text-red-600' : 'text-yellow-600'}`}>
                      {item.priceVariation.toFixed(1)}%
                    </span>
                    <div className="text-sm text-gray-500">
                      {item.minPrice.toFixed(2)} - {item.maxPrice.toFixed(2)} ₽
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Рендер отчета "Активность пользователей"
   * @returns {JSX.Element} - Компонент для рендеринга
   */
  const renderUserActivityReport = () => {
    const data = reportData['user-activity'];
    if (!data || !data.allUsers || data.allUsers.length === 0) {
      return <NoDataMessage />;
    }
    
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">Активность пользователей</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Активных пользователей</p>
            <p className="text-2xl font-medium">{data.totalUsers}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Всего ценников</p>
            <p className="text-2xl font-medium">{data.totalPriceTags}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Среднее на пользователя</p>
            <p className="text-2xl font-medium">{data.averagePriceTagsPerUser.toFixed(1)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Лучший результат</p>
            <p className="text-2xl font-medium">{data.topPerformers[0]?.totalPriceTags || 0}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium mb-4">Топ-5 пользователей</h4>
            <div className="space-y-3">
              {data.topPerformers.map((user, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <div>
                    <span className="font-medium">{user.userName}</span>
                    <div className="text-sm text-gray-500">
                      Верификация: {user.verificationRate.toFixed(1)}%
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-blue-600">{user.totalPriceTags}</span>
                    <div className="text-sm text-gray-500">ценников</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium mb-4">Сбор по категориям</h4>
            <div className="space-y-3">
              {data.topPerformers.slice(0, 5).map((user, idx) => (
                <div key={idx} className="border-b border-gray-100 pb-3">
                  <div className="font-medium mb-2">{user.userName}</div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center">
                      <div className="text-red-600 font-medium">{user.categoriesCollected.minimum}</div>
                      <div className="text-gray-500">Мин</div>
                    </div>
                    <div className="text-center">
                      <div className="text-blue-600 font-medium">{user.categoriesCollected.average}</div>
                      <div className="text-gray-500">Ср</div>
                    </div>
                    <div className="text-center">
                      <div className="text-green-600 font-medium">{user.categoriesCollected.maximum}</div>
                      <div className="text-gray-500">Макс</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h4 className="font-medium mb-4">Детальная статистика по всем пользователям</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left">Пользователь</th>
                  <th className="px-4 py-2 text-right">Всего ценников</th>
                  <th className="px-4 py-2 text-right">Верифицировано</th>
                  <th className="px-4 py-2 text-right">% верификации</th>
                  <th className="px-4 py-2 text-right">Магазинов</th>
                  <th className="px-4 py-2 text-right">Товаров</th>
                  <th className="px-4 py-2 text-right">Первая активность</th>
                  <th className="px-4 py-2 text-right">Последняя активность</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.allUsers.map((user, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{user.userName}</td>
                    <td className="px-4 py-2 text-right">{user.totalPriceTags}</td>
                    <td className="px-4 py-2 text-right text-green-600">{user.verifiedPriceTags}</td>
                    <td className="px-4 py-2 text-right">{user.verificationRate.toFixed(1)}%</td>
                    <td className="px-4 py-2 text-right">{user.storesWorked}</td>
                    <td className="px-4 py-2 text-right">{user.productsScanned}</td>
                    <td className="px-4 py-2 text-right text-sm text-gray-500">
                      {new Date(user.firstActivity).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-gray-500">
                      {new Date(user.lastActivity).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Рендер отчета "Статистика верификации"
   * @returns {JSX.Element} - Компонент для рендеринга
   */
  const renderVerificationStatsReport = () => {
    const data = reportData['verification-stats'];
    if (!data) {
      return <NoDataMessage />;
    }
    
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">Статистика верификации ценников</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Всего ценников</p>
            <p className="text-2xl font-medium">{data.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Верифицировано</p>
            <p className="text-2xl font-medium text-green-600">{data.verified}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Не верифицировано</p>
            <p className="text-2xl font-medium text-red-600">{data.unverified}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Процент верификации</p>
            <p className="text-2xl font-medium">{data.verificationRate.toFixed(1)}%</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {data.verificationByUser && data.verificationByUser.length > 0 ? (
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="font-medium mb-2">Верификация по пользователям</h4>
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Пользователь</th>
                    <th className="px-4 py-2 text-right">Верифицировано</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.verificationByUser.map((user, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{user.name}</td>
                      <td className="px-4 py-2 text-right">{user.verified}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="font-medium mb-2">Верификация по пользователям</h4>
              <p className="text-gray-500 text-center py-4">Нет данных по пользователям</p>
            </div>
          )}
          
          {data.verificationByStoreType && data.verificationByStoreType.length > 0 ? (
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="font-medium mb-2">Верификация по типам магазинов</h4>
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Тип магазина</th>
                    <th className="px-4 py-2 text-right">Процент верификации</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.verificationByStoreType.map((store, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{store.type}</td>
                      <td className="px-4 py-2 text-right">{store.rate.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="font-medium mb-2">Верификация по типам магазинов</h4>
              <p className="text-gray-500 text-center py-4">Нет данных по типам магазинов</p>
            </div>
          )}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="h-60 flex items-center justify-center">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500">
                Здесь будут графики статистики верификации по времени.<br/>
                В реальном приложении можно использовать библиотеку визуализации.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Рендер тела активного отчета
   * @returns {JSX.Element} - Компонент для рендеринга
   */
  const renderActiveReport = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <p>{error}</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'store-comparison':
        return renderStoreComparisonReport();
      case 'price-comparison':
        return renderPriceComparisonReport();
      case 'user-activity':
        return renderUserActivityReport();
      case 'category-analytics':
        return renderCategoryAnalyticsReport();
      case 'units-analytics':
        return <UnitsAnalyticsReport />;
      case 'images-analytics':
        return <ImagesAnalyticsReport />;
      default:
        return <div>Выберите отчет</div>;
    }
  };

  return (
    <div>
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Отчеты</h1>
          <p className="text-gray-600">Аналитика и отчеты по собранным данным</p>
        </div>
        
        <div className="mt-4 md:mt-0 flex gap-2">
          <button
            onClick={handleExportCsv}
            className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            CSV
          </button>
          
          <button
            onClick={handleExportExcel}
            className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Excel
          </button>
          
          <button
            onClick={handleExportPdf}
            className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            PDF
          </button>
        </div>
      </header>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-medium">Типы отчетов</h2>
            </div>
            
            <div className="p-2">
              <ul>
                <li>
                  <button
                    className={`w-full text-left px-4 py-2 rounded-md ${activeTab === 'store-comparison' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                    onClick={() => setActiveTab('store-comparison')}
                  >
                    Сравнение магазинов
                  </button>
                </li>
                <li>
                  <button
                    className={`w-full text-left px-4 py-2 rounded-md ${activeTab === 'price-comparison' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                    onClick={() => setActiveTab('price-comparison')}
                  >
                    Сравнение цен
                  </button>
                </li>
                <li>
                  <button
                    className={`w-full text-left px-4 py-2 rounded-md ${activeTab === 'user-activity' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                    onClick={() => setActiveTab('user-activity')}
                  >
                    Активность пользователей
                  </button>
                </li>
                <li>
                  <button
                    className={`w-full text-left px-4 py-2 rounded-md ${activeTab === 'category-analytics' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                    onClick={() => setActiveTab('category-analytics')}
                  >
                    Аналитика по категориям
                  </button>
                </li>
                <li>
                  <button
                    className={`w-full text-left px-4 py-2 rounded-md ${activeTab === 'units-analytics' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                    onClick={() => setActiveTab('units-analytics')}
                  >
                    Аналитика единиц измерения
                  </button>
                </li>
                <li>
                  <button
                    className={`w-full text-left px-4 py-2 rounded-md ${activeTab === 'images-analytics' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                    onClick={() => setActiveTab('images-analytics')}
                  >
                    Аналитика изображений
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Кнопка для отчета 1С */}
          <a
            href="/reports/1c"
            className="block bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg shadow-sm p-4 text-center font-medium transition-colors mb-4"
          >
            <div className="flex items-center justify-center mb-2">
              <svg
                className="w-6 h-6 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Отчет для 1С
            </div>
            <span className="text-xs opacity-90">Выгрузка средних цен</span>
          </a>

          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-medium">Фильтры</h2>
            </div>
            
            <div className="p-4">
              <form onSubmit={handleApplyFilters}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Товар</label>
                  <select
                    name="productId"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.productId}
                    onChange={handleFilterChange}
                  >
                    <option value="">Все товары</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>{product.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Магазин</label>
                  <select
                    name="storeId"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.storeId}
                    onChange={handleFilterChange}
                  >
                    <option value="">Все магазины</option>
                    {stores.map((store) => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Категория</label>
                  <select
                    name="categoryId"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.categoryId}
                    onChange={handleFilterChange}
                  >
                    <option value="">Все категории</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Период</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <input
                        type="date"
                        name="startDate"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div>
                      <input
                        type="date"
                        name="endDate"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
                >
                  Применить фильтры
                </button>
              </form>
            </div>
          </div>
        </div>
        
        <div className="md:col-span-3">
          <div className="bg-white rounded-lg shadow-sm p-6">
            {renderActiveReport()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;